import React from "react";
import { motion } from "framer-motion";

export function AuroraBackground() {
    return (
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
            {/* Background pattern grid */}
            <div className="absolute inset-0 grid-lines opacity-70 dark:opacity-80" />
            
            {/* Animated radial vignette */}
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/80" />
            <div className="absolute inset-0 bg-gradient-to-r from-background/40 via-transparent to-background/40" />

            {/* Glowing floating aurora orbs */}
            <div 
                className="absolute top-[-10%] left-[10%] w-[450px] sm:w-[600px] h-[450px] sm:h-[600px] rounded-full blob opacity-25 dark:opacity-20 pointer-events-none blur-[120px] sm:blur-[140px]" 
                style={{ background: "radial-gradient(circle, #3B82F6, #8B5CF6 60%, transparent)" }} 
            />
            <div 
                className="absolute bottom-[-10%] right-[15%] w-[400px] sm:w-[550px] h-[400px] sm:h-[550px] rounded-full blob opacity-20 dark:opacity-15 pointer-events-none blur-[120px] sm:blur-[140px]" 
                style={{ background: "radial-gradient(circle, #06B6D4, #3B82F6 70%, transparent)", animationDelay: "-6s" }} 
            />
            <div 
                className="absolute top-[40%] right-[-5%] w-[350px] sm:w-[500px] h-[350px] sm:h-[500px] rounded-full blob opacity-15 dark:opacity-15 pointer-events-none blur-[130px]" 
                style={{ background: "radial-gradient(circle, #8B5CF6, #EC4899 70%, transparent)", animationDelay: "-12s" }} 
            />
        </div>
    );
}

export function Reveal({ children, delay = 0, className = "", direction = "up", duration = 0.65 }) {
    const variants = {
        hidden: { 
            opacity: 0, 
            y: direction === "up" ? 28 : direction === "down" ? -28 : 0,
            x: direction === "left" ? 28 : direction === "right" ? -28 : 0,
            scale: 0.98
        },
        visible: { 
            opacity: 1, 
            y: 0, 
            x: 0,
            scale: 1,
            transition: { 
                duration, 
                delay, 
                ease: [0.21, 0.47, 0.32, 0.98] 
            } 
        },
    };

    return (
        <motion.div
            variants={variants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

export function SectionHeading({ id, index, subtitle, title, description, className = "" }) {
    return (
        <div id={id} className={`mb-12 md:mb-16 scroll-mt-28 ${className}`}>
            <Reveal duration={0.5}>
                <div className="flex items-center gap-2.5 mb-3">
                    <span className="w-2 h-2 rounded-full bg-gradient-to-r from-primary to-accent animate-pulse" />
                    {index && <span className="font-mono text-xs md:text-sm uppercase tracking-widest text-primary font-semibold">{index}</span>}
                    {subtitle && !index && <span className="font-mono text-xs md:text-sm uppercase tracking-widest text-primary font-semibold">{subtitle}</span>}
                </div>
            </Reveal>
            
            {title && (
                <Reveal delay={0.08} duration={0.6}>
                    <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-foreground max-w-3xl leading-[1.15]">
                        {title}
                    </h2>
                </Reveal>
            )}
            
            {description && (
                <Reveal delay={0.15} duration={0.6}>
                    <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
                        {description}
                    </p>
                </Reveal>
            )}
        </div>
    );
}
